export type EventType =
  | 'appointment'
  | 'birthday'
  | 'school'
  | 'sport'
  | 'social'
  | 'work'
  | 'holiday'
  | 'meal'
  | 'chore'
  | 'reminder'
  | 'arts'
  | 'other';

export type RecurrenceType =
  | 'none'
  | 'daily'
  | 'weekdays'   // Mon–Fri only
  | 'weekly'
  | 'biweekly'   // every 2 weeks
  | 'monthly'
  | 'yearly'
  | 'custom';    // specific days of week chosen by user

export interface ExternalRSVP {
  id: string;
  guest_name: string;
  status: 'accepted' | 'maybe' | 'declined';
  adults_count: number;
  kids_count: number;
  dietary_notes?: string;
  checked_in?: boolean;
  approval_status?: 'approved' | 'pending' | 'rejected';
  submitted_at: string;
}

export interface PlannerEvent {
  id: string;
  family_id?: string;
  title: string;
  type: EventType;
  startDate: string;   // ISO date string YYYY-MM-DD
  startTime?: string;  // HH:mm (24h)
  endDate: string;
  endTime?: string;    // HH:mm (24h)
  allDay: boolean;
  color?: string;      // hex or tailwind
  notes?: string;
  location?: string;
  attendees?: string[]; // member names/ids
  rsvps?: Record<string, 'accepted' | 'declined' | 'maybe'>; // user_id -> status map
  enable_duty_claiming?: boolean;
  duty_claims?: Record<string, 'driving' | 'attending' | 'snacks'>;
  enable_public_rsvp?: boolean;
  rsvp_mode?: 'open' | 'invitee_list';
  preapproved_guests?: string[];
  hide_guestlist_from_invitees?: boolean;
  rsvp_passcode?: string;
  rsvp_require_approval?: boolean;
  enable_door_checkin?: boolean;
  external_rsvps?: Record<string, ExternalRSVP>;
  recurrence?: RecurrenceType;
  recurrenceDays?: number[]; // 0=Sun…6=Sat, used when recurrence='custom'
  recurrenceEndDate?: string; // ISO date — when to stop repeating
  reminders?: number[]; // array of minutes before the event
  notifyTimeToLeave?: boolean; // Smart departure alert
  travelMode?: 'DRIVING' | 'TRANSIT' | 'WALKING'; // Mode for smart departure travel time
  isCompleted?: boolean;
  isKitchenMeal?: boolean; // identification for Kitchen integration
  linkedRecipeId?: string; // for meal events
  googleEventId?: string;  // ID from Google Calendar for two-way sync
  googleCalendarId?: string; // ID of the originating Google Calendar
  calendarName?: string;   // Display name of originating calendar
  excludedDates?: string[]; // ISO date strings YYYY-MM-DD for instances that were deleted
  visibility?: 'private' | 'shared' | 'custom';
  owner_id?: string;
  shared_with?: string[];
  isCountdown?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  appointment: '🏥 Appointment',
  birthday: '🎂 Birthday',
  school: '📚 School',
  sport: '⚽ Sport',
  social: '🎉 Social',
  work: '💼 Work',
  holiday: '✈️ Holiday',
  meal: '🍽️ Meal',
  chore: '🧹 Chore',
  reminder: '⏰ Reminder',
  arts: '🎨 Arts',
  other: '📌 Other',
};

export const EVENT_TYPE_COLORS: Record<EventType, string> = {
  appointment: '#ef4444',
  birthday: '#ec4899',
  school: '#3b82f6',
  sport: '#10b981',
  social: '#f59e0b',
  work: '#6366f1',
  holiday: '#f97316',
  meal: '#84cc16',
  chore: '#22c55e',
  reminder: '#8b5cf6',
  arts: '#d946ef',
  other: '#6b7280',
};
